import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import {
  performBuildingsMassAction,
  setStaffMemberBuildings,
} from '../../../api'
import {
  BUILDING_SHORT_LIST_API,
  BUILDING_SINGLE_FIELD_API,
} from '../../../constants/api'
import { BUILDING_ENTITY } from '../../../constants/entity'
import { SUBMENU_PARAM_ACTIVE_VALUE } from '../../../constants/route'
import { ScreenContainerRef } from '../../../context'
import {
  useInitData,
  useNotification,
  useRegisterReload,
  useReloadCountersAfterRefetch,
} from '../../../hooks'
import { getQaSelector } from '../../../utils/common'
import {
  BuildingFormModal,
  BuildingTableLayout,
  getBuildingFormErrorNotification,
  getBuildingFormSuccessNotification,
  getBuildingItemActions,
  getBuildingMassActions,
  useBuildingsActions,
  useBuildingTablePage,
} from '../../Building'
import Button from '../../Button'
import { FILTER_BAR_FILTER_TRIGGER_VARIANT, FilterBar } from '../../Filter'
import { PageActions, PageLoader } from '../../Page'
import { getEmptyStateActions, PageEmptyState } from '../../PageEmptyState'
import { TablePagination } from '../../TablePagination'
import {
  getEntityTabFilterStateApi,
  updateEntityTabTableConfig,
} from '../EntityTab.api'
import { getEntityTabFilterBarApiUrl } from '../EntityTab.utils'

import { ENTITY_TAB_BUILDINGS_DEFAULT_QA_NAME } from './EntityTabBuildings.constants'
import { getEntityTabBuildingsScopeFilter } from './EntityTabBuildings.utils'
import { EntityTabBuildingsEditModal } from './EntityTabBuildingsEditModal'

import * as styles from './EntityTabBuildings.module.css'

import BuildingsIcon from '../../../icons/building.svg'
import EditIcon from '../../../icons/edit.svg'
import PlusIcon from '../../../icons/plus.svg'

function EntityTabBuildings({
  canEdit = null,
  creationInitialValues,
  editButtonTextKey = 'Building.EditBuildingsBtnKey',
  entity = BUILDING_ENTITY,
  getCreationPayload,
  getItemActions = getBuildingItemActions,
  getMassActions = getBuildingMassActions,
  listApi = BUILDING_SHORT_LIST_API,
  qaName = ENTITY_TAB_BUILDINGS_DEFAULT_QA_NAME,
  scopeFilterKey,
  scopeId,
  shouldShowCreateAction = true,
  onClickEdit = null,
  onRegisterReload,
  onReloadCounters,
  onRemoveFromGroup,
}) {
  const { t } = useTranslation()

  const [modal, setModal] = useState(null)

  const [isSavingBuildings, setSavingBuildings] = useState(false)

  function handleCloseModal() {
    setModal(null)
  }

  async function handleApplyTableConfig(data) {
    return updateEntityTabTableConfig(data, entity.plural)
  }

  const tablePage = useBuildingTablePage({
    asyncTableOptions: {
      extraFilters: getEntityTabBuildingsScopeFilter({
        scopeFilterKey,
        scopeId,
      }),
      filterStateApi: getEntityTabFilterStateApi({
        entityPlural: entity.plural,
        objectId: scopeId,
        sub: SUBMENU_PARAM_ACTIVE_VALUE,
      }),
      listApi,
      withoutSubMenu: true,
    },
    entity,
    extraSelectionResetDeps: [scopeId],
    sub: SUBMENU_PARAM_ACTIVE_VALUE,
    withBrowserTabTitle: false,
    onApplyTableConfig: handleApplyTableConfig,
  })

  const {
    availableFilters,
    excludeIds,
    filterValues,
    handleChangeShouldReloadFilters,
    handleCleanSelection,
    handleLoadInit,
    handleReload,
    handleReloadTableAfterMassAction,
    handleSetFilterValues,
    handleSetPagination,
    hasFilterChanged,
    isInitialLoadSettled,
    isLoading,
    isRefetching,
    items,
    ordering,
    pageScrollWrapperRef,
    pagination,
    permissions,
    selectAllPages,
    selectedIds,
    shouldReloadFilters,
  } = tablePage

  const { handleReloadWithoutCounters } = useReloadCountersAfterRefetch({
    isRefetching,
    reload: handleReloadTableAfterMassAction,
    onReloadCounters,
  })

  useEffect(() => {
    if (scopeId) {
      handleLoadInit()
    }
  }, [scopeId])

  const canCreate =
    shouldShowCreateAction && (permissions.can_create || isLoading)

  const { handleAddErrorNotification, handleAddSuccessNotification } =
    useNotification()

  const { availableSections } = useInitData()

  function handleErrorCreation() {
    handleAddErrorNotification(getBuildingFormErrorNotification())
  }

  function handleSuccessCreation(data) {
    handleCloseModal()
    handleReload()
    handleAddSuccessNotification(
      getBuildingFormSuccessNotification({ ...data, availableSections })
    )
  }

  function handleOpenCreation() {
    setModal(
      <BuildingFormModal
        getCreationPayload={getCreationPayload}
        initialValues={creationInitialValues}
        onClose={handleCloseModal}
        onError={handleErrorCreation}
        onSuccess={handleSuccessCreation}
      />
    )
  }

  function handleSubmitEditBuildings({ buildingIds }) {
    handleCloseModal()
    setSavingBuildings(true)

    setStaffMemberBuildings({ data: { buildings: buildingIds }, id: scopeId })
      .then(() => {
        handleCleanSelection()
        handleReloadTableAfterMassAction()
        handleAddSuccessNotification('Notification.CommonTitleChangedKey')
      })
      .catch(() => {
        handleAddErrorNotification('Error.CommonApiRequestFailKey')
      })
      .finally(() => {
        setSavingBuildings(false)
      })
  }

  function handleOpenEditBuildings() {
    setModal(
      <EntityTabBuildingsEditModal
        scopeFilterKey={scopeFilterKey}
        scopeId={scopeId}
        onClose={handleCloseModal}
        onSubmit={handleSubmitEditBuildings}
      />
    )
  }

  useRegisterReload({
    isReady: isInitialLoadSettled,
    reload: handleReloadWithoutCounters,
    onRegisterReload,
  })

  const handleClickEdit = onClickEdit ?? handleOpenEditBuildings

  async function handlePerformMassAction(params) {
    return performBuildingsMassAction({
      ...params,
      filters: {
        ...params.filters,
        ...getEntityTabBuildingsScopeFilter({ scopeFilterKey, scopeId }),
      },
    })
  }

  const { isPerformingMassAction, setItemAction, setMassAction } =
    useBuildingsActions({
      excludeIds,
      filterValues,
      pagination,
      performApi: handlePerformMassAction,
      selectAllPages,
      selectedIds,
      sub: SUBMENU_PARAM_ACTIVE_VALUE,
      onCleanSelection: handleCleanSelection,
      onCloseModal: handleCloseModal,
      onReload: handleReloadTableAfterMassAction,
      onRemoveFromGroup,
      onSetModal: setModal,
    })

  const massActions = getMassActions({
    permissions,
    sub: SUBMENU_PARAM_ACTIVE_VALUE,
    t,
  })

  const hasBuildings = items?.items?.length > 0

  const shouldCollapseToolbar = !hasBuildings && !hasFilterChanged && !isLoading

  const canEditBuildings = canEdit ?? (permissions.can_edit || isLoading)

  const emptyStateActions = [
    ...getEmptyStateActions({
      Component: Button.White,
      Icon: EditIcon,
      actionName: qaName.editBuildings,
      isLoading,
      qaName: qaName.editBuildings,
      shouldShowActions: canEditBuildings && !hasFilterChanged,
      text: t(editButtonTextKey),
      onClick: handleClickEdit,
    }),
    ...getEmptyStateActions({
      actionName: qaName.addNew,
      isLoading,
      qaName: qaName.addNew,
      shouldShowActions: canCreate && !hasFilterChanged,
      text: t('BuildingTable.AddNewBtnKey'),
      onClick: handleOpenCreation,
    }),
  ]

  return (
    <ScreenContainerRef value={pageScrollWrapperRef}>
      <div
        {...getQaSelector({ value: qaName.table })}
        className={styles.EntityTabBuildings}
        ref={pageScrollWrapperRef}
      >
        {isPerformingMassAction || isRefetching || isSavingBuildings ? (
          <PageLoader />
        ) : null}
        {shouldCollapseToolbar ? null : (
          <div className={styles.toolbar}>
            <FilterBar
              responsive
              apiUrl={getEntityTabFilterBarApiUrl({
                baseApi: BUILDING_SINGLE_FIELD_API,
                scopeFilterKey,
                scopeId,
              })}
              availableFilters={availableFilters}
              className={styles.filterBar}
              entity={entity}
              filterTriggerVariant={
                FILTER_BAR_FILTER_TRIGGER_VARIANT.COMPACT_ICON
              }
              filterValues={filterValues}
              isSkeleton={isLoading}
              objectId={scopeId}
              ordering={ordering}
              pagination={{ per_page: pagination?.pageSize }}
              setFilterValues={handleSetFilterValues}
              shouldReloadValues={shouldReloadFilters}
              sub={SUBMENU_PARAM_ACTIVE_VALUE}
              onChangeShouldReloadValues={handleChangeShouldReloadFilters}
              onReload={handleReload}
            />
            <PageActions className={styles.actions} isLoading={isLoading}>
              {canEditBuildings ? (
                <Button.Text
                  beforeIconProps={{ Icon: EditIcon }}
                  isSkeleton={isLoading}
                  qaName={qaName.editBuildings}
                  text={t(editButtonTextKey)}
                  onClick={handleClickEdit}
                />
              ) : null}
              {canCreate ? (
                <Button.Text
                  beforeIconProps={{ Icon: PlusIcon }}
                  isSkeleton={isLoading}
                  qaName={qaName.addNew}
                  text={t('BuildingTable.AddNewBtnKey')}
                  onClick={handleOpenCreation}
                />
              ) : null}
            </PageActions>
          </div>
        )}
        <BuildingTableLayout
          emptyState={
            <PageEmptyState
              EntityIcon={BuildingsIcon}
              actions={emptyStateActions}
              text={t('Building.NoDataKey')}
            />
          }
          getBuildingItemActions={getItemActions}
          hasTable={hasBuildings || isLoading}
          massActions={massActions}
          modal={modal}
          setItemAction={setItemAction}
          setMassAction={setMassAction}
          tablePage={tablePage}
        />
        <TablePagination
          responsive
          isSkeleton={isLoading}
          value={pagination}
          onChange={handleSetPagination}
        />
      </div>
    </ScreenContainerRef>
  )
}

export default EntityTabBuildings
